/**
 * WebGL fighter + exhaust instanced meshes, explosion/fireworks instanced meshes,
 * and per-frame fighter draw. Parallel to render/canvas2d/fighters.js.
 */

import { EntityType, FIGHTER_COLORS } from '../../entities.js';
import { getUnitColor } from '../../render_utils.js';
import { iconColorForEntity } from '../canvas2d/primitives.js';
import {
    Mesh, Geometry, Shader, GlProgram, UniformGroup, Buffer,
} from './pixi.js';
import {
    FIGHTER_VERT, FIGHTER_FRAG, FIGHTER_FLOATS, MAX_FIGHTERS,
    EXHAUST_VERT, EXHAUST_FRAG,
    EXPLOSION_VERT, EXPLOSION_FRAG, EXPLOSION_FLOATS,
    FIREWORKS_VERT, FIREWORKS_FRAG, FIREWORKS_FLOATS,
    hexToRgb01,
} from './shaders.js';

// -----------------------------------------------------------------------
// Mesh builders (called once from _initPixi)
// -----------------------------------------------------------------------

/**
 * Build the fighter body + exhaust + ship exhaust instanced meshes.
 * Sets wr._fighterMesh, wr._fighterGeo, wr._fighterShader, wr._fighterBuf,
 * wr._exhaustMesh, wr._exhaustGeo, wr._shipExhaustMesh, wr._shipExhaustBuf,
 * wr._shipExhaustGeo.
 * @param {object} wr  WebGLRenderer instance
 */
export function buildFighterMesh(wr) {
    const quadIdx = new Uint16Array([0,1,2, 0,2,3]);

    const fighterQuadBuf = new Buffer({ data: new Float32Array([-0.5,-0.5, 0.5,-0.5, 0.5,0.5, -0.5,0.5]), usage: 32|1024 });
    const exhaustQuadBuf = new Buffer({ data: new Float32Array([-1.5,-0.5, 1.5,-0.5, 1.5,0.5, -1.5,0.5]), usage: 32|1024 });

    const instBuf   = new Buffer({ data: wr._fighterData, usage: 32 });
    wr._fighterBuf  = instBuf;

    const stride = FIGHTER_FLOATS * 4;
    const instanceAttribs = (buf) => ({
        aPos:          { buffer: buf, format: 'float32x2', stride, offset: 0,  instance: true },
        aVel:          { buffer: buf, format: 'float32x2', stride, offset: 8,  instance: true },
        aColor:        { buffer: buf, format: 'float32x3', stride, offset: 16, instance: true },
        aAlpha:        { buffer: buf, format: 'float32',   stride, offset: 28, instance: true },
        aWeight:       { buffer: buf, format: 'float32',   stride, offset: 32, instance: true },
    });

    const uniforms = new UniformGroup({ uM: { value: wr._uMData, type: 'mat3x3<f32>' } });

    wr._fighterGeo = new Geometry({
        attributes: { aQuad: { buffer: fighterQuadBuf, format: 'float32x2', stride: 8, offset: 0 }, ...instanceAttribs(instBuf) },
        indexBuffer: quadIdx,
        instanceCount: 0,
    });
    const fProg = new GlProgram({ vertex: FIGHTER_VERT, fragment: FIGHTER_FRAG, name: 'fighter-shader' });
    wr._fighterShader = new Shader({ glProgram: fProg, resources: { uniforms } });
    wr._fighterMesh   = new Mesh({ geometry: wr._fighterGeo, shader: wr._fighterShader });

    const eProg = new GlProgram({ vertex: EXHAUST_VERT, fragment: EXHAUST_FRAG, name: 'exhaust-shader' });
    wr._exhaustShader = new Shader({ glProgram: eProg, resources: { uniforms } });

    wr._exhaustGeo = new Geometry({
        attributes: { aQuad: { buffer: exhaustQuadBuf, format: 'float32x2', stride: 8, offset: 0 }, ...instanceAttribs(instBuf) },
        indexBuffer: quadIdx,
        instanceCount: 0,
    });
    wr._exhaustMesh           = new Mesh({ geometry: wr._exhaustGeo, shader: wr._exhaustShader });
    wr._exhaustMesh.blendMode = 'add';

    const shipInstBuf         = new Buffer({ data: wr._shipData, usage: 32 });
    wr._shipExhaustBuf        = shipInstBuf;
    const exhaustQuadBuf2     = new Buffer({ data: new Float32Array([-1.5,-0.5, 1.5,-0.5, 1.5,0.5, -1.5,0.5]), usage: 32|1024 });
    const eShipProg           = new GlProgram({ vertex: EXHAUST_VERT, fragment: EXHAUST_FRAG, name: 'ship-exhaust-shader' });
    const shipUniforms        = new UniformGroup({ uM: { value: wr._uMData, type: 'mat3x3<f32>' } });
    const shipExhaustShader   = new Shader({ glProgram: eShipProg, resources: { uniforms: shipUniforms } });
    wr._shipExhaustGeo  = new Geometry({
        attributes: { aQuad: { buffer: exhaustQuadBuf2, format: 'float32x2', stride: 8, offset: 0 }, ...instanceAttribs(shipInstBuf) },
        indexBuffer: new Uint16Array([0,1,2, 0,2,3]),
        instanceCount: 0,
    });
    wr._shipExhaustMesh           = new Mesh({ geometry: wr._shipExhaustGeo, shader: shipExhaustShader });
    wr._shipExhaustMesh.blendMode = 'add';
}

/**
 * Build the fireworks instanced mesh.
 * Sets wr._fireworksBuf, wr._fireworksUniforms, wr._fireworksGeo, wr._fireworksMesh.
 * @param {object} wr
 */
export function buildFireworksMesh(wr) {
    const stride  = FIREWORKS_FLOATS * 4;
    const quadBuf = new Buffer({ data: new Float32Array([-1,-1, 1,-1, 1,1, -1,1]), usage: 32|1024 });
    const instBuf = new Buffer({ data: wr._fireworksData, usage: 32 });
    wr._fireworksBuf = instBuf;

    wr._fireworksUniforms = new UniformGroup({ uM: { value: wr._uMData, type: 'mat3x3<f32>' } });
    const prog   = new GlProgram({ vertex: FIREWORKS_VERT, fragment: FIREWORKS_FRAG, name: 'fireworks-shader' });
    const shader = new Shader({ glProgram: prog, resources: { uniforms: wr._fireworksUniforms } });

    wr._fireworksGeo = new Geometry({
        attributes: {
            aQuad:   { buffer: quadBuf, format: 'float32x2', stride: 8, offset: 0,  instance: false },
            aCenter: { buffer: instBuf, format: 'float32x2', stride, offset: 0,  instance: true },
            aRadius: { buffer: instBuf, format: 'float32',   stride, offset: 8,  instance: true },
            aTime:   { buffer: instBuf, format: 'float32',   stride, offset: 12, instance: true },
            aSeed:   { buffer: instBuf, format: 'float32',   stride, offset: 16, instance: true },
            aColor:  { buffer: instBuf, format: 'float32x3', stride, offset: 20, instance: true },
        },
        indexBuffer: new Uint16Array([0,1,2, 0,2,3]),
        instanceCount: 0,
    });
    wr._fireworksMesh           = new Mesh({ geometry: wr._fireworksGeo, shader });
    wr._fireworksMesh.blendMode = 'add';
}

/**
 * Build the explosion instanced mesh.
 * Sets wr._explosionBuf, wr._explosionUniforms, wr._explosionGeo, wr._explosionMesh.
 * @param {object} wr
 */
export function buildExplosionMesh(wr) {
    const stride  = EXPLOSION_FLOATS * 4;
    const quadBuf = new Buffer({ data: new Float32Array([-1,-1, 1,-1, 1,1, -1,1]), usage: 32|1024 });
    const instBuf = new Buffer({ data: wr._explosionData, usage: 32 });
    wr._explosionBuf = instBuf;

    wr._explosionUniforms = new UniformGroup({ uM: { value: wr._uMData, type: 'mat3x3<f32>' } });
    const prog = new GlProgram({ vertex: EXPLOSION_VERT, fragment: EXPLOSION_FRAG, name: 'explosion-shader' });
    const shader = new Shader({ glProgram: prog, resources: { uniforms: wr._explosionUniforms } });

    wr._explosionGeo = new Geometry({
        attributes: {
            aQuad:       { buffer: quadBuf, format: 'float32x2', stride: 8, offset: 0,  instance: false },
            aCenter:     { buffer: instBuf, format: 'float32x2', stride, offset: 0,  instance: true },
            aRadius:     { buffer: instBuf, format: 'float32',   stride, offset: 8,  instance: true },
            aProgress:   { buffer: instBuf, format: 'float32',   stride, offset: 12, instance: true },
            aSeed:       { buffer: instBuf, format: 'float32',   stride, offset: 16, instance: true },
            aOuterColor: { buffer: instBuf, format: 'float32x3', stride, offset: 20, instance: true },
        },
        indexBuffer: new Uint16Array([0,1,2, 0,2,3]),
        instanceCount: 0,
    });
    wr._explosionMesh           = new Mesh({ geometry: wr._explosionGeo, shader });
    wr._explosionMesh.blendMode = 'add';
}

// -----------------------------------------------------------------------
// Per-frame
// -----------------------------------------------------------------------

/**
 * Update the world→clip matrix for all instanced shaders.
 * Must be called after zoom or transform changes.
 * @param {object} wr
 */
export function updateFighterProjection(wr) {
    const [a, b, c, d, e, f] = wr._inner._gameTf;
    const zoom = wr._zoom;
    const W    = wr._inner.gridSystem.width;
    const H    = wr._inner.gridSystem.height;
    const offX = W / 2 * (1 - zoom);
    const offY = H / 2 * (1 - zoom);
    const a2   = a * zoom,  b2 = b * zoom;
    const c2   = c * zoom,  d2 = d * zoom;
    const e2   = a * offX + c * offY + e;
    const f2   = b * offX + d * offY + f;
    const cw   = wr._glCanvas.width;
    const ch   = wr._glCanvas.height;
    const m    = wr._uMData;
    // Column-major mat3, Y-negated for RT rendering (GL native Y-axis convention).
    m[0] =  2*a2/cw;   m[1] =  2*b2/ch;   m[2] = 0;
    m[3] =  2*c2/cw;   m[4] =  2*d2/ch;   m[5] = 0;
    m[6] = 2*e2/cw-1;  m[7] = 2*f2/ch-1;  m[8] = 1;
    wr._fighterShader.resources.uniforms._dirtyId++;
    wr._shipExhaustMesh.shader.resources.uniforms._dirtyId++;
    if (wr._explosionUniforms)  wr._explosionUniforms._dirtyId++;
    if (wr._fireworksUniforms)  wr._fireworksUniforms._dirtyId++;
}

/**
 * Per-frame: pack fighter and ship exhaust instance data into GPU buffers.
 * @param {object} wr
 */
export function drawFighters(wr) {
    const g     = wr._fighterGraphics;
    const units = wr._inner.entitySystem.units;
    const time  = wr._inner.entitySystem.time;
    const data  = wr._fighterData;

    g.clear();
    let count = 0;

    const stealthPhase = (time % 4.0) / 4.0;

    for (let i = 0; i < units.length; i++) {
        const e = units[i];
        if (e.type !== EntityType.FIGHTER) continue;
        if (count >= MAX_FIGHTERS) break;

        const alpha    = e.visual?.alpha ?? 1.0;
        const colorHex = getUnitColor(e, FIGHTER_COLORS);
        const [r, gr, b] = hexToRgb01(colorHex);
        const baseW    = e.subtype === 'mega' ? 1.2 : e.subtype === 'heavy' ? 1.1 : e.subtype === 'medium' ? 1.0 : 0.9;
        const sw       = baseW * (e.size / 2);
        const base     = count * FIGHTER_FLOATS;
        data[base]   = e.x;
        data[base+1] = e.y;
        data[base+2] = e.velX / 10;
        data[base+3] = e.velY / 10;
        data[base+4] = r;
        data[base+5] = gr;
        data[base+6] = b;
        data[base+7] = alpha;
        data[base+8] = sw;
        count++;

        if (e.stealthCycleTime > 0) {
            const glint = 1.0 - Math.abs(stealthPhase - 0.5) / 0.07;
            if (glint > 0) {
                const rr = 4;
                for (let j = 0; j < 4; j++) {
                    const a2 = (j * Math.PI) / 4;
                    g.moveTo(e.x, e.y)
                        .lineTo(e.x + Math.cos(a2) * rr, e.y + Math.sin(a2) * rr)
                        .stroke({ color: 0xFFFFFF, width: 0.8, alpha: glint * 0.8 });
                }
            }
        }
    }

    const nFighters = count;
    wr._fighterGeo.instanceCount = nFighters;
    wr._exhaustGeo.instanceCount = nFighters;
    wr._fighterBuf.update();

    const shipData = wr._shipData;
    let nShips = 0;
    for (let i = 0; i < units.length; i++) {
        const e = units[i];
        if (e.type !== EntityType.CORVETTE && e.type !== EntityType.FRIGATE && e.type !== EntityType.MISSILE) continue;
        if (nShips >= 512) break;

        const alpha    = e.visual?.alpha ?? 1.0;
        const colorHex = iconColorForEntity(e);
        const [r, gr, b] = hexToRgb01(colorHex);
        const halfSize = e.size * 0.5;
        const speedMag = Math.hypot(e.velX, e.velY);
        const sw       = halfSize * 0.4;
        const base     = nShips * FIGHTER_FLOATS;
        shipData[base]   = e.x;
        shipData[base+1] = e.y;
        shipData[base+2] = speedMag > 0.5 ? e.velX / speedMag * halfSize : 0;
        shipData[base+3] = speedMag > 0.5 ? e.velY / speedMag * halfSize : 0;
        shipData[base+4] = r;
        shipData[base+5] = gr;
        shipData[base+6] = b;
        shipData[base+7] = alpha;
        shipData[base+8] = sw;
        nShips++;
    }
    wr._shipExhaustGeo.instanceCount = nShips;
    wr._shipExhaustBuf.update();

    updateFighterProjection(wr);
}
